import { RubricAskAgentSection } from '@/components/admin/evaluation-rubrics/rubric-ask-agent-section'
import { RubricCorpusHero } from '@/components/admin/evaluation-rubrics/rubric-corpus-hero'
import { RubricDimensionsSection } from '@/components/admin/evaluation-rubrics/rubric-dimensions-section'
import { RubricFocusAreasSection } from '@/components/admin/evaluation-rubrics/rubric-focus-areas-section'
import { RubricRequirementsSection } from '@/components/admin/evaluation-rubrics/rubric-requirements-section'
import { RubricRevisionHistorySection } from '@/components/admin/evaluation-rubrics/rubric-revision-history-section'
import { RubricScoringSection } from '@/components/admin/evaluation-rubrics/rubric-scoring-section'
import { RubricSkillDomainsSection } from '@/components/admin/evaluation-rubrics/rubric-skill-domains-section'
import type { RubricSectionId } from '@/components/admin/evaluation-rubrics/rubric-section-ids'
import type { ScoringConfig } from '@/components/admin/evaluation-rubrics/rubric-read-helpers'
import type { RubricCriterion, RubricRevisionSummary } from '@/hooks/use-evaluation-rubrics-admin'
import type { ScoringPresetKey } from '@/lib/rubric-publish-payload'
import type { ReactNode } from 'react'

type SkillDomain = {
  key?: string
  label?: string
  skills?: string[]
}

export type RubricCaseFileProps = {
  label: string
  methodologyId: string
  status: string
  revisionVersion?: number
  validationMessage?: string | null
  draftCriteria: RubricCriterion[]
  scoring: ScoringConfig
  scoringPreset: ScoringPresetKey | 'custom'
  skillDomains: SkillDomain[]
  skillsCatalogPath: string
  revisions: RubricRevisionSummary[]
  highlightedSection?: RubricSectionId | null
  testCard?: ReactNode
  onChangeCriteria: (criteria: RubricCriterion[]) => void
  onAskAgent?: (message: string) => void
  onEditDimensionsAdvanced: () => void
  onEditFocusLevelsSkills: (criterionKey?: string) => void
  onEditRequirementsAdvanced: () => void
  onEditScoring: () => void
  onViewAllHistory: () => void
}

export function RubricCaseFile({
  label,
  methodologyId,
  status,
  revisionVersion,
  validationMessage,
  draftCriteria,
  scoring,
  scoringPreset,
  skillDomains,
  skillsCatalogPath,
  revisions,
  highlightedSection,
  testCard,
  onChangeCriteria,
  onAskAgent,
  onEditDimensionsAdvanced,
  onEditFocusLevelsSkills,
  onEditRequirementsAdvanced,
  onEditScoring,
  onViewAllHistory,
}: RubricCaseFileProps) {
  function isHighlighted(section: RubricSectionId) {
    return highlightedSection === section
  }

  return (
    <div className="space-y-4">
      <RubricCorpusHero
        label={label}
        methodologyId={methodologyId}
        status={status}
        version={revisionVersion}
        validationMessage={validationMessage}
      />

      <RubricAskAgentSection
        highlighted={isHighlighted('describe')}
        onAskAgent={onAskAgent}
      />

      <RubricDimensionsSection
        criteria={draftCriteria}
        highlighted={isHighlighted('dimensions')}
        onChange={onChangeCriteria}
        onEditAdvanced={onEditDimensionsAdvanced}
      />

      <RubricFocusAreasSection
        criteria={draftCriteria}
        highlighted={isHighlighted('focus')}
        onChange={onChangeCriteria}
        onEditLevelsSkills={onEditFocusLevelsSkills}
      />

      <RubricRequirementsSection
        criteria={draftCriteria}
        highlighted={isHighlighted('requirements')}
        onChange={onChangeCriteria}
        onEditAdvanced={onEditRequirementsAdvanced}
        defaultOpen={false}
      />

      <RubricScoringSection
        scoring={scoring}
        scoringPreset={scoringPreset}
        highlighted={isHighlighted('scoring')}
        onEdit={onEditScoring}
        defaultOpen={false}
      />

      <RubricSkillDomainsSection
        skillDomains={skillDomains}
        revisionVersion={revisionVersion}
        skillsCatalogPath={skillsCatalogPath}
        defaultOpen={false}
      />

      {testCard}

      <RubricRevisionHistorySection
        revisions={revisions}
        highlighted={isHighlighted('history')}
        onViewAll={onViewAllHistory}
        defaultOpen={false}
      />
    </div>
  )
}
